import React, { useContext } from 'react';
import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Images } from 'utils';
import {
    GOAL_BLOCK_ATTR,
    GOAL_BLOCK_CLASS,
    GOAL_BLOCK_ID_ATTR,
    GOAL_BLOCK_VALUE_ATTR,
    TARGET_BLOCK_ATTR,
    newInlineFieldId,
    readGoalBlockValue,
    writeGoalBlockValue,
} from '../../../utils/inlineFields';
import { InlineFillContext } from './inlineFillContext';
import { chipNodeViewOptions, keepNodesPlugin } from './inlineFieldExtension';
// Circular via inlineFieldsRichText.js; only used at render time.
import { GoalBlockFill } from './goalBlockFill';
import richTextGoalBlock from "../../../../../assets/images/icons/richTextGoalBlock.svg";

/**
 * TipTap nodes for the progress report Goal block (see `utils/inlineFields.js`
 * for the stored shape).
 *
 * - `goalBlock` sits in a Rich Text. In the editor it is a chip on its own
 *   line, like an inline field's; clicking it selects it for the Properties
 *   panel and opens the Goal design modal.
 * - `targetBlock` sits in the goal design and marks where each goal's targets
 *   go. In the design modal it is the same kind of chip; clicking it switches
 *   the modal to the target design.
 *
 * Both are registered in every editor so stored text always round-trips.
 * Filling a note (`fill` option, `RichTextElement fill`), a `goalBlock` draws
 * its goal picker and picked goals (`GoalBlockFill`) and keeps the picks on
 * its own `value` attribute; staff cannot delete it there.
 */

/**
 * The chip both blocks draw in the editor, styled like an inline field's. The
 * wrapper is the chip and takes the click, as `InlineFieldChip` does.
 */
const BlockChip = ({ label, required, selected, onClick }) => (
    <NodeViewWrapper
        className={`inline-field-chip goal-block-chip${selected ? ' inline-field-chip-selected' : ''}`}
        contentEditable={false}
        data-drag-handle=""
        onClick={onClick}
    >
        <img src={Images.richTextGoalBlock} alt="richTextGoalBlock" className="inline-field-chip-icon" />
        {label}
        {required && <span className="inline-field-required">*</span>}
    </NodeViewWrapper>
);

const GoalBlockView = ({ node, selected, extension, updateAttributes }) => {
    const { block, blockId } = node.attrs;
    const fill = useContext(InlineFillContext);
    if (fill) {
        return (
            <NodeViewWrapper className="goal-block-fill-node" contentEditable={false}>
                <GoalBlockFill
                    block={block}
                    value={readGoalBlockValue(node.attrs.value)}
                    disabled={fill.disabled}
                    error={!!fill.errorFor(blockId)}
                    showErrors={fill.showErrors}
                    hideStaffOnly={fill.hideStaffOnly}
                    imageUrls={fill.imageUrls}
                    onChange={(next) => updateAttributes({ value: writeGoalBlockValue(next) })}
                />
            </NodeViewWrapper>
        );
    }
    return (
        <BlockChip
            label="Goal Block"
            required={block?.properties?.required}
            selected={selected}
            onClick={() => extension.options.onSelect?.(blockId)}
        />
    );
};

const dedupeKey = new PluginKey('goalBlockDedupe');

export const GoalBlock = Node.create({
    name: 'goalBlock',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    addOptions() {
        return {
            /** `(blockId) => void` — the chip was clicked. */
            onSelect: null,
            /** A note being filled: the block is its goal picker and cannot be deleted. */
            fill: false,
        };
    },

    addAttributes() {
        return {
            blockId: { default: null, rendered: false },
            block: { default: null, rendered: false },
            value: { default: '', rendered: false },
        };
    },

    parseHTML() {
        return [
            {
                tag: `div[${GOAL_BLOCK_ATTR}]`,
                priority: 60,
                getAttrs: (dom) => {
                    let block = null;
                    try {
                        block = JSON.parse(dom.getAttribute(GOAL_BLOCK_ATTR));
                    } catch {
                        return false;
                    }
                    return {
                        blockId: dom.getAttribute(GOAL_BLOCK_ID_ATTR) || newInlineFieldId(),
                        block,
                        value: dom.getAttribute(GOAL_BLOCK_VALUE_ATTR) || '',
                    };
                },
            },
        ];
    },

    // Same shape as `goalBlockHtml` in utils/inlineFields.js.
    renderHTML({ node }) {
        const { block, blockId, value } = node.attrs;
        return [
            'div',
            {
                class: GOAL_BLOCK_CLASS,
                [GOAL_BLOCK_ATTR]: JSON.stringify(block || {}),
                [GOAL_BLOCK_ID_ATTR]: blockId,
                ...(value ? { [GOAL_BLOCK_VALUE_ATTR]: value } : {}),
            },
            block?.label || '',
        ];
    },

    renderText({ node }) {
        return node.attrs.block?.label || '';
    },

    addNodeView() {
        return ReactNodeViewRenderer(GoalBlockView, chipNodeViewOptions(this.options.fill));
    },

    addCommands() {
        return {
            insertGoalBlock:
                ({ blockId, block }) =>
                ({ commands }) =>
                    commands.insertContent([{ type: this.name, attrs: { blockId, block, value: '' } }, { type: 'paragraph' }]),
        };
    },

    /** Like inline fields: a pasted copy gets a fresh id and no picked goals. */
    addProseMirrorPlugins() {
        return [
            ...(this.options.fill ? [keepNodesPlugin('goalBlockKeep', this.name, 'blockId')] : []),
            new Plugin({
                key: dedupeKey,
                appendTransaction: (transactions, _old, state) => {
                    if (!transactions.some((tr) => tr.docChanged)) return null;
                    const seen = new Set();
                    let tr = null;
                    state.doc.descendants((node, pos) => {
                        if (node.type.name !== this.name) return;
                        const id = node.attrs.blockId;
                        if (id && !seen.has(id)) {
                            seen.add(id);
                            return;
                        }
                        tr = tr || state.tr;
                        tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockId: newInlineFieldId(), value: '' });
                    });
                    return tr;
                },
            }),
        ];
    },
});

const TargetBlockView = ({ selected, extension }) => (
    <BlockChip label="Target block" selected={selected} onClick={() => extension.options.onSelect?.()} />
);

export const TargetBlock = Node.create({
    name: 'targetBlock',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    addOptions() {
        return {
            /** `() => void` — the chip was clicked. */
            onSelect: null,
        };
    },

    parseHTML() {
        return [{ tag: `div[${TARGET_BLOCK_ATTR}]`, priority: 60 }];
    },

    renderHTML() {
        return ['div', { class: 'target-block', [TARGET_BLOCK_ATTR]: '' }, 'Target block'];
    },

    renderText() {
        return 'Target block';
    },

    addNodeView() {
        return ReactNodeViewRenderer(TargetBlockView, chipNodeViewOptions(false));
    },

    addCommands() {
        return {
            insertTargetBlock:
                () =>
                ({ commands }) =>
                    commands.insertContent([{ type: this.name }, { type: 'paragraph' }]),
        };
    },
});
